import React from "react";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadialBarChart,
  RadialBar,
} from "recharts";

// A vibrant palette for per-game and prize distribution charts
const PALETTE = [
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#84cc16", // lime
  "#a855f7", // purple
  "#f97316", // orange
  "#14b8a6", // teal
  "#eab308", // yellow
];
export const CHART_PALETTE = PALETTE;

export function PlaysTimeseries({ data = [] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="grad-plays" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.45} />
            <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="date"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          tickFormatter={(d) => d.slice(5)}
        />
        <YAxis
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "12px",
            fontSize: "12px",
          }}
        />
        <Area
          type="monotone"
          dataKey="plays"
          stroke="#8b5cf6"
          strokeWidth={2.4}
          fill="url(#grad-plays)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function CtaBar({ data = [] }) {
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="type"
          fontSize={10}
          stroke="hsl(var(--muted-foreground))"
        />
        <YAxis
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "12px",
            fontSize: "12px",
          }}
        />
        <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function MerchantSparkline({ data = [], color = "#8b5cf6" }) {
  const gradientId = `spark-${color.replace("#", "")}`;
  return (
    <ResponsiveContainer width="100%" height={48}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.5} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Tooltip
          cursor={false}
          contentStyle={{
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: "11px",
            padding: "4px 8px",
          }}
          labelFormatter={(d) =>
            typeof d === "string" && d.length >= 10 ? d.slice(5) : ""
          }
          formatter={(v) => [v, "plays"]}
        />
        <Area
          type="monotone"
          dataKey="plays"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}


const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "12px",
  fontSize: "12px",
};

/**
 * Horizontal bar chart comparing plays / leads / wins per game.
 * Expects `data` = [{ title, plays, leads, wins }].
 */
export function PerGameBars({ data = [] }) {
  if (!data.length) return null;
  const maxLen = 22;
  const trimmed = data.map((g) => ({
    ...g,
    name: g.title && g.title.length > maxLen ? g.title.slice(0, maxLen - 1) + "…" : g.title,
  }));
  const height = Math.max(180, trimmed.length * 42 + 40);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={trimmed}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
        barCategoryGap={10}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="hsl(var(--border))"
          horizontal={false}
        />
        <XAxis
          type="number"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          width={130}
        />
        <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--muted))" }} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
        <Bar dataKey="plays" fill="#8b5cf6" radius={[0, 6, 6, 0]} name="Plays" />
        <Bar dataKey="leads" fill="#3b82f6" radius={[0, 6, 6, 0]} name="Leads" />
        <Bar dataKey="wins" fill="#10b981" radius={[0, 6, 6, 0]} name="Wins" />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Donut chart: distribution of plays per game.
 * Expects `data` = [{ title, plays }].
 */
export function PlaysDonut({ data = [] }) {
  const filtered = (data || []).filter((g) => g.plays > 0);
  if (!filtered.length) return null;
  const sliced = filtered.slice(0, 8);
  return (
    <ResponsiveContainer width="100%" height={260}>
      <PieChart>
        <Pie
          data={sliced}
          dataKey="plays"
          nameKey="title"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={2}
          stroke="hsl(var(--card))"
          strokeWidth={2}
        >
          {sliced.map((_, i) => (
            <Cell key={`c-${i}`} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        <Legend
          wrapperStyle={{ fontSize: 11, paddingLeft: 12 }}
          iconType="circle"
          layout="vertical"
          align="right"
          verticalAlign="middle"
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

/**
 * Conversion-rate ranking per game. `data` = [{ title, conversion }].
 */
export function ConversionBar({ data = [] }) {
  if (!data.length) return null;
  const rows = [...data]
    .filter((g) => g.plays > 0)
    .sort((a, b) => (b.conversion || 0) - (a.conversion || 0))
    .slice(0, 10)
    .map((g) => ({
      name: g.title && g.title.length > 22 ? g.title.slice(0, 21) + "…" : g.title,
      conversion: g.conversion || 0,
    }));
  if (!rows.length) return null;
  const height = Math.max(180, rows.length * 36 + 40);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
        <XAxis
          type="number"
          unit="%"
          domain={[0, 100]}
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
        />
        <YAxis
          type="category"
          dataKey="name"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          width={130}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(v) => [`${v}%`, "Conversion"]}
          cursor={{ fill: "hsl(var(--muted))" }}
        />
        <Bar dataKey="conversion" radius={[0, 6, 6, 0]}>
          {rows.map((_, i) => (
            <Cell key={`c-${i}`} fill={PALETTE[i % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Hourly activity bar chart — 24 bars, 0..23h.
 * `data` = [{ hour: 0..23, plays }].
 */
export function HourlyActivity({ data = [] }) {
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="grad-hourly" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#ec4899" stopOpacity={0.9} />
            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.6} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis
          dataKey="hour"
          fontSize={10}
          stroke="hsl(var(--muted-foreground))"
          tickFormatter={(h) => `${String(h).padStart(2, "0")}h`}
          interval={1}
        />
        <YAxis fontSize={11} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
        <Tooltip
          contentStyle={tooltipStyle}
          cursor={{ fill: "hsl(var(--muted))" }}
          labelFormatter={(h) => `${String(h).padStart(2, "0")}h – ${String((h + 1) % 24).padStart(2, "0")}h`}
          formatter={(v) => [v, "plays"]}
        />
        <Bar dataKey="plays" fill="url(#grad-hourly)" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Prize distribution — horizontal bar chart showing which prizes are won
 * the most. `data` = [{ label, count }].
 */
export function PrizeDistribution({ data = [] }) {
  if (!data.length) return null;
  const rows = data.map((p) => ({
    name: p.label && p.label.length > 26 ? p.label.slice(0, 25) + "…" : p.label,
    count: p.count,
  }));
  const height = Math.max(180, rows.length * 36 + 40);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
        <XAxis
          type="number"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          fontSize={11}
          stroke="hsl(var(--muted-foreground))"
          width={160}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          cursor={{ fill: "hsl(var(--muted))" }}
          formatter={(v) => [v, "wins"]}
        />
        <Bar dataKey="count" radius={[0, 6, 6, 0]}>
          {rows.map((_, i) => (
            <Cell key={`c-${i}`} fill={PALETTE[(i + 3) % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Radial conversion gauge — big centered percentage in a filled arc.
 * Used for the overview conversion KPI.
 */
export function ConversionGauge({ value = 0, color = "#8b5cf6" }) {
  const v = Math.min(100, Math.max(0, Number(value) || 0));
  const data = [{ name: "conv", value: v }];
  const label = `${Math.round(v * 10) / 10}%`;
  return (
    <div className="relative w-full" style={{ height: 180 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadialBarChart
          data={data}
          innerRadius="72%"
          outerRadius="100%"
          startAngle={90}
          endAngle={-270}
          barSize={14}
        >
          <defs>
            <linearGradient id="grad-gauge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={color} stopOpacity={0.9} />
              <stop offset="100%" stopColor="#ec4899" stopOpacity={0.9} />
            </linearGradient>
          </defs>
          <RadialBar
            dataKey="value"
            cornerRadius={12}
            fill="url(#grad-gauge)"
            background={{ fill: "hsl(var(--muted))" }}
            minAngle={0}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="text-3xl font-semibold tabular-nums">{label}</span>
      </div>
    </div>
  );
}
